import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Radio, Row, Checkbox } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { PROMOTION } from '../../../../constants/define/translation';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { ShopType, ShopStatus, SHOP_TYPE } from '../../../../types/shop';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';
import { DragDropImageVideo } from '../../../molecules/upload';
import { CountryContext } from '../../../../repository/context/countryContext';
import { PreviewPromotion } from '../../../molecules/preview/common/PreviewPromotion';
import {
  patchUpdateTileWidgetRepository,
  postCreateTileWidgetRepository,
} from '../../../../repository/mytm/shop/shopRepository';
import { URLType, URLTypePromotionList } from '../../../../constants/common';

interface FormPromotionProps {
  record?: ShopType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  countrySelected: string;
}
const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const plainOptions = [
  { label: 'Par', value: 'Par' },
  { label: 'Birdie', value: 'Birdie' },
  { label: 'Eagle', value: 'Eagle' },
];

export const FormPromotion = (props: FormPromotionProps) => {
  const { t } = useTranslation(PROMOTION);
  const { record, onRefresh, onCloseModal, setDataRecord, countrySelected } = props;
  const options = record?.options ? JSON.parse(record.options) : null;
  const defaultValue = {
    ctaLink: record?.ctaLink || '',
    countries: record ? record?.countries : countrySelected || 'USA',
    loyaltyTier: options?.loyalty_tier || ['Par', 'Birdie', 'Eagle'],
    ctaLinkType: options?.ctaLinkType || URLType.PDP,
  };
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [videoList, setVideoList] = useState<UploadFileToBlobInterface[]>([]);
  const [listTier, setListTier] = useState<any>(options?.loyalty_tier || ['Par', 'Birdie', 'Eagle']);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);

  const checkAll = plainOptions.length === listTier.length;
  const countryContext = useContext(CountryContext);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  useEffect(() => {
    if (imageList.length || videoList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList, videoList]);

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSetVideoList = useCallback((data: UploadFileToBlobInterface[]) => {
    setVideoList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      setDataForm((old: any) => ({
        ...old,
        ctaLink: newField?.ctaLink,
        ctaLinkType: newField?.ctaLinkType,
      }));
    },
    [form],
  );

  const onCheckAllChange = useCallback(
    e => {
      const isAllSelected = e.target.checked;
      if (isAllSelected) {
        setListTier(['Par', 'Birdie', 'Eagle']);
        form.setFieldsValue({ loyaltyTier: ['Par', 'Birdie', 'Eagle'] });
      } else {
        setListTier([]);
        form.setFieldsValue({ loyaltyTier: [] });
      }
    },
    [form],
  );

  const onChangeCheckbox = useCallback(
    e => {
      setListTier(e);
      form.setFieldsValue({ loyaltyTier: e });
    },
    [form],
  );

  const onFinish = useCallback(
    (formValue: any) => {
      if (!(imageList.length || videoList.length)) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }

      setCreateLoading(true);
      let imgList = imageList.filter(img => !img.isImageURL);
      let imgLink = imageList.filter(img => img.isImageURL);
      if (isEmpty(imgList)) {
        imgList = videoList.filter(img => !img.isVideoURL);
      }

      if (isEmpty(imgLink)) {
        imgLink = videoList.filter(img => img.isVideoURL);
      }
      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const videoType = !!videoList[0]?.fileType?.includes('video');
          const url = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];
          const newOptions = {
            loyalty_tier: listTier,
            ctaLinkType: formValue?.ctaLinkType,
          };
          const payloadToPost = {
            type: SHOP_TYPE.PROMOTION,
            title: SHOP_TYPE.PROMOTION,
            countries: formValue?.countries?.toString(),
            ctaLink: formValue?.ctaLink,
            status: record?.status || ShopStatus.INACTIVE,
            imageLink: (!videoType && url) || null,
            videoLink: (videoType && url) || null,
            options: newOptions,
          };
          if (record) {
            return patchUpdateTileWidgetRepository({
              ...payloadToPost,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updatePromotion'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateTileWidgetRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createPromotion'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList, videoList, listTier],
  );

  useEffect(() => {
    if (record) {
      setImageList(record?.imageLink ? [{ dataURL: record?.imageLink, isImageURL: true }] : []);
      setVideoList(
        record?.videoLink ? [{ dataURL: record?.videoLink || null, isVideoURL: true, fileType: 'video' }] : [],
      );
      setSelectedImage(0);
      setDataForm(record);
      form.setFieldsValue({ countries: record?.countries });
    }
  }, [record]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-promotion">
            <Row className="form-promotion-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImageVideo
                  {...{
                    imageList,
                    onSetImageList,
                    setMessageErrorUploadImage,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    videoList,
                    onSetVideoList,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewPromotion
                        values={{
                          ...dataForm,
                          backgroundImage: imageList?.length && imageList[0].dataURL,
                          backgroundVideo: (videoList?.length && videoList[0].dataURL) || null,
                          describeText: t('form.uploadImage'),
                          describeText2: t('form.image3x4'),
                        }}
                        height="300px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormPromotion span={15}>
                <div className="form-scroll">
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="countries"
                        label={t('form.countries')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="loyaltyTier"
                        label={t('form.loyaltyTier')}
                        rules={[{ required: true, message: t('validate.loyaltyTierBlank') }]}
                      >
                        <Checkbox onChange={onCheckAllChange} checked={checkAll}>
                          All
                        </Checkbox>
                        <Checkbox.Group
                          value={listTier}
                          options={plainOptions}
                          onChange={onChangeCheckbox}
                          disabled={listTier.length === 3}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="ctaLinkType"
                        label={t('form.linkType')}
                        rules={[{ required: true, message: t('validate.linkTypeBlank') }]}
                        values={URLTypePromotionList}
                        textDisplay="text"
                        onChange={onChangeInput}
                        showSearch
                        allowClear
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24} className="buttonLink">
                      <Form.Item name="ctaLink" label={t('form.link')}>
                        <Input placeholder={t('form.link')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdatePromotion') : t('form.btnCreatePromotion')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormPromotion>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-promotion-container {
    padding: 1.5rem;

    .form-promotion__description {
      resize: none;
    }
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormPromotion = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }
`;
